import { isSameCountry } from './locationUtils';

/**
 * Calculate estimated cost based on distance and international status
 * @param {number} distanceInMeters - Distance in meters
 * @param {boolean} isInternational - Whether this is international shipping
 * @returns {number} - Estimated shipping cost
 */
export const calculateEstimatedCost = (distanceInMeters, isInternational = false) => {
  // Base shipping rate 
  let baseRate = 5.99;
  
  // International shipping has higher base rate
  if (isInternational) {
    baseRate = 19.99; // Higher base rate for international
    console.log('🌎 International shipping detected - using base rate of $19.99');
  } else {
    console.log('🏠 Domestic shipping - using base rate of $5.99');
  }
  
  // Distance in miles
  const distanceInMiles = distanceInMeters / 1609.34;
  
  // Simple calculation: base rate + additional per mile
  let cost = baseRate;
  
  if (isInternational) {
    // International: steeper increase per mile
    cost += distanceInMiles * 0.015;
  } else {
    // Domestic: only add cost after first 20 miles
    if (distanceInMiles > 20) {
      cost += (distanceInMiles - 20) * 0.10;
    }
  }
  
  console.log('💰 SHIPPING COST DETAILS', {
    baseRate,
    distanceInMiles: distanceInMiles.toFixed(2),
    additionalCost: isInternational 
      ? (distanceInMiles * 0.015).toFixed(2) 
      : (Math.max(0, distanceInMiles - 20) * 0.10).toFixed(2),
    totalBeforeCap: cost.toFixed(2),
    cap: isInternational ? 59.99 : 29.99
  });
  
  // Cap at reasonable maximum - higher for international
  return Math.min(cost, isInternational ? 59.99 : 29.99);
};

/**
 * Determine if shipping is international based on address/location data
 * @param {Object} fromAddress - Seller address 
 * @param {Object} toAddress - Buyer address or location
 * @returns {boolean} - Whether shipping is international
 */
export const isInternationalShipping = (fromAddress, toAddress) => {
  if (!fromAddress || !toAddress) return false;
  
  // Get country data from either code or name
  const fromCountry = fromAddress.countryCode || fromAddress.country;
  const toCountry = toAddress.countryCode || toAddress.country;
  
  // If either country is missing, we can't determine if it's international
  if (!fromCountry || !toCountry) {
    console.log('⚠️ Cannot determine if international - missing country data');
    // Default to domestic
    return false;
  }
  
  // Use the isSameCountry utility to properly compare countries
  const isSame = isSameCountry(fromCountry, toCountry);
  
  console.log('🌎 International shipping check:', {
    fromCountry,
    toCountry,
    isInternational: !isSame,
    normalized: !isSame ? 'Different countries' : 'Same country'
  });
  
  return !isSame;
};

/**
 * Estimate delivery time based on distance and international status
 * @param {number} distanceInMeters - Distance in meters
 * @param {boolean} isInternational - Whether this is international shipping
 * @returns {string} - Estimated delivery time range (e.g., "2-4 days")
 */
export const estimateDeliveryTime = (distanceInMeters, isInternational = false) => {
  if (isInternational) {
    // International shipping times are longer
    if (distanceInMeters > 8000000) {
      return '10-15 days'; // Very long international (e.g., US to Asia)
    } else if (distanceInMeters > 5000000) {
      return '7-10 days';  // Medium international (e.g., US to Europe)
    } else {
      return '5-7 days';   // Shorter international (e.g., US to Canada/Mexico)
    }
  } else {
    // Domestic shipping times
    if (distanceInMeters > 1000000) {
      return '4-6 days';   // Cross-country
    } else if (distanceInMeters > 500000) {
      return '2-4 days';   // Regional
    } else {
      return '1-2 days';   // Local
    }
  }
};

/**
 * Format distance for display using appropriate units
 * @param {number} meters - Distance in meters
 * @param {Object} userLocation - User location with country code
 * @param {Object} sellerAddress - Seller address with country code
 * @returns {string} - Formatted distance string
 */
export const formatDistance = (meters, userLocation, sellerAddress) => {
  if (meters === null) return '';
  
  // Check if international shipping
  const isInternational = 
    sellerAddress?.country && userLocation?.country && 
    sellerAddress.country !== userLocation.country;
    
  // Use miles for US, km otherwise
  const inMiles = meters / 1609.34;
  const inKm = meters / 1000;
  
  // For very short distances
  if (inMiles < 1) {
    return `${Math.round(inMiles * 5280)} feet away`;
  }
  
  // If either address is in US, show miles, otherwise km
  const useMetric = userLocation?.country !== 'US' && sellerAddress?.country !== 'US';
  
  if (useMetric) {
    return `${Math.round(inKm)} km away`;
  } else {
    return `${Math.round(inMiles)} miles away`;
  }
};